import axiosInstance from './axiosInstance';
import type { PublicOrderTracking } from '../../types/publicOrderTracking.types';

export interface CreateOrderDto {
  userId?: string;
  guestEmail?: string;
  shippingAddress: {
    name: string;
    email: string;
    phone?: string;
    address: string;
    addressLine2?: string;
    city: string;
    state?: string;
    zip: string;
    country: string;
    deliveryInstructions?: string;
    landmark?: string;
  };
  shippingSelection?: {
    countryIso2: string;
    regionId?: string;
    cityId?: string;
    otherCityName?: string;
    shippingZoneId?: string;
    landmark?: string;
  };
  paymentMethod: 'CARD' | 'PAYPAL' | 'APPLE_PAY' | 'CASH_ON_DELIVERY';
  deliveryOption?: string;
  items: Array<{
    designId: string;
    productId: string;
    productVariantId: string;
    selectedColor?: string;
    quantity: number;
    cartItemId?: string;
  }>;
  customerNotes?: string;
  referralCode?: string;
  referralSessionId?: string;
}

export interface OrderItem {
  id: string;
  designId: string;
  productId: string;
  productVariantId: string;
  selectedColor?: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  designSnapshot: {
    title: string;
    frontSide: any;
    backSide: any;
    frontPreviewUrl?: string;
    backPreviewUrl?: string;
  };
  productSnapshot: {
    name: string;
    description?: string;
    slug: string;
  };
  variantSnapshot: {
    color: string;
    colorLabel?: string;
    hexCode?: string;
    size: string;
    sku: string;
    fit?: 'regular' | 'oversize';
    gender?: 'men' | 'women' | 'unisex';
    price: number;
  };
  printStatus: string;
  /** Per-line fulfillment (buyer sees partial returns/refunds here). */
  itemFulfillmentStatus?: string;
  itemPaymentStatus?: string;
  itemRefundedAt?: string | null;
  itemReturnedAt?: string | null;
}

export interface OrderRefundSummary {
  originalOrderTotal: number;
  totalRefundedAmount: number;
  netPaidAmount: number;
  refundedItemIds: string[];
  derivation: string;
}

export interface Order {
  id: string;
  orderNumber: string;
  userId?: string;
  guestEmail?: string;
  status: 'PENDING' | 'PROCESSING' | 'CONFIRMED' | 'SHIPPED' | 'DELIVERED' | 'CANCELLED';
  subtotal: number;
  shippingCost: number;
  shippingNeedsReview?: boolean;
  tax: number;
  discount: number;
  total: number;
  shippingAddress: {
    name: string;
    email: string;
    phone?: string;
    company?: string;
    address: string;
    addressLine2?: string;
    city: string;
    state?: string;
    zip: string;
    country: string;
    deliveryInstructions?: string;
  };
  deliveryOption: string;
  paymentMethod: 'CARD' | 'PAYPAL' | 'APPLE_PAY' | 'CASH_ON_DELIVERY';
  paymentStatus: 'PENDING' | 'PAID' | 'FAILED' | 'REFUNDED';
  /** Roll-up of line fulfillment; order may stay DELIVERED while this is PARTIALLY_REFUNDED. */
  aggregateFulfillmentStatus?: string | null;
  customerNotes?: string;
  items: OrderItem[];
  createdAt: string;
  updatedAt: string;
  refundSummary?: OrderRefundSummary;
  trackingNumber?: string | null;
  carrier?: string | null;
  /** Present only on create-order response for guest checkout; used for confirmation URL. */
  guestOrderAccessToken?: string | null;
}

export const orderService = {
  /**
   * Create a new order
   */
  createOrder: async (orderData: CreateOrderDto): Promise<Order> => {
    const { data } = await axiosInstance.post('/orders', orderData);
    return data;
  },

  /**
   * Get order by ID
   */
  getOrderById: async (orderId: string): Promise<Order> => {
    const { data } = await axiosInstance.get(`/orders/${orderId}`);
    return data;
  },

  /**
   * Get order by order number
   */
  getOrderByNumber: async (orderNumber: string): Promise<Order> => {
    const { data } = await axiosInstance.get(`/orders/number/${orderNumber}`);
    return data;
  },

  /**
   * Public tracking by order number — no auth; no PII or pricing in response.
   */
  getPublicOrderTracking: async (orderNumber: string): Promise<PublicOrderTracking> => {
    const encoded = encodeURIComponent(orderNumber.trim());
    const { data } = await axiosInstance.get<PublicOrderTracking>(
      `/orders/track/${encoded}`,
    );
    return data;
  },

  /**
   * Guest order full details (confirmation) — requires checkout-issued access token.
   */
  getGuestOrder: async (orderId: string, accessToken: string): Promise<Order> => {
    const { data } = await axiosInstance.get(`/orders/guest/${orderId}`, {
      params: { accessToken },
    });
    return data;
  },

  /**
   * Get all orders for the current user (requires authentication)
   * @param params - Optional pagination parameters
   * @returns Orders array or paginated response
   */
  getMyOrders: async (params?: { page?: number; limit?: number }): Promise<Order[] | { orders: Order[]; pagination: any }> => {
    const { data } = await axiosInstance.get('/orders/my/orders', { params });
    return data;
  },

  /**
   * Update order status (admin)
   */
  updateOrderStatus: async (orderId: string, status: string): Promise<Order> => {
    const { data } = await axiosInstance.patch(`/orders/${orderId}/status`, { status });
    return data;
  },

  /**
   * Cancel order (user-facing, only allowed before DESIGN_RECEIVED)
   */
  cancelOrder: async (orderId: string, reason?: string): Promise<Order> => {
    const { data } = await axiosInstance.post(`/orders/${orderId}/cancel`, { reason });
    return data;
  },
};

